import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import type { LabOrder } from "@/types/labs";
import { labDate, labDisplayName } from "./labPresentation";
import { labFlag, labRecords } from "./labBiomarkers";
import { LabResultCard, LabSummary } from "./LabResults";
import { LabPdfDownload } from "./LabPdfDownload";
import { LabReportDialog } from "./LabOrderContent";
import { LabStatus } from "./LabBiomarkerDetail";
import { useLabEvent } from "./labExperiment";

export function LabResultsOverview({ orders }: { orders: LabOrder[] }) {
  const records = labRecords(orders);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const emit = useLabEvent();
  const selected =
    records.find((record) => record.result.id === recordId) ?? records[0];
  if (!selected) return null;
  const { order, result } = selected;
  return (
    <section aria-label="Your biomarker results" className="ph-no-capture">
      <div className="flex flex-wrap justify-between gap-15 items-end mb-15">
        <div>
          <p className="type-label-xs text-ink-secondary">
            Your lab results
          </p>
          <h2 className="font-display text-28 mt-8">Explore your results.</h2>
        </div>
        {records.length > 1 && (
          <label className="type-label-xs text-ink-secondary">
            Report
            <select
              aria-label="Choose lab report"
              value={result.id}
              onChange={(event) => {
                setRecordId(event.target.value);
                setReportOpen(false);
              }}
              className="block mt-6 min-h-44 border border-line-subtle bg-page px-12 text-14 max-w-full"
            >
              {records.map((record) => (
                <option key={record.result.id} value={record.result.id}>
                  {labDate(
                    record.result.collected_at || record.result.created_at,
                  )}{" "}
                  · {labDisplayName(record.order.product_name)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <LabResultCard
        key={`${order.id}:${result.id}`}
        result={result}
        records={records}
        onReport={() => {
          setReportOpen(true);
          emit("blood_test_report_opened", { source: "labs_summary" });
        }}
        download={
          result.report_available && (
            <LabPdfDownload orderId={order.id} resultId={result.id} />
          )
        }
      />
      <LabReportDialog
        key={result.id}
        orderId={order.id}
        resultId={reportOpen ? result.id : null}
        reportCount={result.report_count ?? 1}
        clinician={false}
        onClose={() => setReportOpen(false)}
      />
    </section>
  );
}

export function LabHomeResults({ orders }: { orders: LabOrder[] }) {
  const emit = useLabEvent();
  const record = labRecords(orders).find(
    (record) => record.result.observations.length,
  );
  if (!record) return null;
  const { order, result } = record;
  const items = result.observations
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        Number(labFlag(b.item) === "flagged") -
        Number(labFlag(a.item) === "flagged"),
    )
    .slice(0, 3);
  return (
    <section
      className="ph-no-capture mt-20 border border-line-subtle bg-page rounded-card overflow-hidden"
      aria-label="Your biomarkers"
    >
      <header className="flex justify-between items-start gap-10 p-20 border-b border-line-subtle">
        <div>
          <h2 className="font-display text-24">Your biomarkers</h2>
          <p className="type-body-sm text-ink-secondary mt-8">
            Collected {labDate(result.collected_at || result.created_at)}
          </p>
        </div>
        <Link
          to={`/app/labs/${order.id}#results`}
          className="min-h-44 inline-flex items-center gap-5 text-13 underline shrink-0"
          onClick={() =>
            emit("blood_test_results_summary_clicked", { source: "home" })
          }
        >
          View all <ArrowUpRight size={14} />
        </Link>
      </header>
      <div className="p-20">
        <LabSummary items={result.observations} compact />
      </div>
      <ul>
        {items.map(({ item, index }) => (
          <li key={index} className="border-t border-line-subtle">
            <Link
              to={`/app/labs/${order.id}#results`}
              state={{ labMarker: { resultId: result.id, index } }}
              onClick={() =>
                emit("blood_test_biomarker_viewed", { source: "home" })
              }
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-10 p-15 hover:bg-raised"
            >
              <div>
                <p className="type-body-md leading-130">{item.name}</p>
                <div className="mt-8">
                  <LabStatus item={item} />
                </div>
              </div>
              <div className="text-right">
                <p className="font-display text-18 tabular-nums">
                  {item.value_text}
                </p>
                <ArrowUpRight
                  size={14}
                  className="ml-auto mt-10 text-ink-secondary"
                  aria-hidden
                />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
